import { ChangeDetectorRef, Component, inject, OnInit, signal, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserDetails } from '../user-details/user-details.component';
import { ProductDetails } from '../product-details/product-details.component';
import { UserConfigComponent } from '../user-config/user-config.component';
import { MatTabsModule } from '@angular/material/tabs';
import { SystemAdminService } from '../../../../services/system-admin.service';
import { AbstractControl, FormBuilder, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { noWhitespaceValidator } from '../../../../utils/no-whitespace.validator';
import { BranchDetailsResponse, companyLandingGrids, UserConfigRequestPayload, UserDetailsRequestPayload, UserDetailsResponse } from '../../../../models/SystemAdmin.model';
import { forkJoin, map, Subscription } from 'rxjs';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../../utils/confirmation-dialog/confirmation-dialog.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { AuthService } from '../../../../auth/auth.service';
import { ToastService } from '../../../../services/toast.service';
import { CompanyType, CreatePasswordIsReset, ToastType } from '../../../../enums/config.enum';
import { EmailChangeConfirmation } from '../../../utils/email-change-confirmation/email-change-confirmation.component';
import { CommonModule } from '@angular/common';
import { UnsavedFormChangesGuard } from '../../../../guards/unsaved-form-changes.guard';
import { CountryISO } from 'ngx-intl-tel-input';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';

@Component({
  selector: 'app-add-user',
  standalone: true,
  imports: [MatTabsModule, MatDialogModule, MatExpansionModule, UserDetails, ProductDetails, 
    UserConfigComponent,CommonModule],
  templateUrl: './add-user.component.html',
  styleUrl: './add-user.component.scss'
})
export class AddUserComponent implements OnInit, OnDestroy, UnsavedFormChangesGuard {
  selectedTab = 0;
  isCreateUser = signal<boolean>(true);
  isTabChange = signal<boolean>(false);
  isAdminCompany = signal(false);
   companyList = signal<companyLandingGrids[]>([]);
  userTitle = signal<string>('');
  userId = signal<string>('');
  isAdminCompanySelected = signal(false);
  router = inject(Router);
  activateRoute = inject(ActivatedRoute);
  dialog = inject(MatDialog);
  authService = inject(AuthService);
  systemAdminService = inject(SystemAdminService);
  toastService = inject(ToastService);
  cdr = inject(ChangeDetectorRef)
  fb: FormBuilder = inject(FormBuilder);
  userDetailsForm = this.fb.group({
    firstName: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$'), Validators.maxLength(128)]],
    lastName: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$'), Validators.maxLength(128)]],
    userName: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$'), Validators.maxLength(128)]],
    companyId: [null, [Validators.required, noWhitespaceValidator]],
    branchId: [null, [Validators.required, noWhitespaceValidator]],
    userRoleId: [null, [Validators.required, noWhitespaceValidator]],
    userEmail: [null, [Validators.required, noWhitespaceValidator, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]],
    phone: [null as string | null, [Validators.required, noWhitespaceValidator]],
    phoneCountryCode: [CountryISO.UnitedStates, [Validators.required, noWhitespaceValidator]],
    mobileNumber: [null as string | null, [Validators.required, noWhitespaceValidator]],
    mobileNumberCountryCode: [CountryISO.UnitedStates, [Validators.required, noWhitespaceValidator]],
    countryCode: [null as string | null],
    iPhoneExt: [null,[Validators.maxLength(6)]],
    IPhone:[""],
    userDetailId: [null],
    isActive: [1], 
  });

  userConfigForm = this.fb.group({
    isEmailverified: [0],
    isQrValidated: [0],
    isResetVerified: [0],
    isLocked: [0],
    resetSession:[0],
  });
  initialFormValue: any;
  private changesSaved: boolean = false;
  countryList = signal<any[]>([]);
  CountryISO = CountryISO;
  phoneUtil = PhoneNumberUtil.getInstance();
  private phoneSub: Subscription = new Subscription();
  private mobileSub: Subscription = new Subscription();
  private phoneCountryCodeSub: Subscription = new Subscription();
  private mobileCountryCodeSub: Subscription = new Subscription();
  private isSubmitting: boolean = false;
  private isBinding: boolean = false;

  private extractDigitsFromTelValue(value: any): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number') {
      return String(value).replace(/\D/g, '');
    }
    // ngx-intl-tel-input can emit an object shape
    if (typeof value === 'object' && value.number !== undefined) {
      return String(value.number).replace(/\D/g, '');
    }
    return '';
  }

  ngOnInit(): void {
    // Set up phone number validators - only required validation, no format validation
    this.userDetailsForm.get('phone')?.setValidators([
      Validators.required,
      noWhitespaceValidator
    ]);
    this.userDetailsForm.get('mobileNumber')?.setValidators([
      Validators.required,
      noWhitespaceValidator
    ]);

    this.getCountryList();
    this.activateRoute.params.subscribe({
      next: (params) => {
        if(params && params['userId']){
          this.isCreateUser.set(false);
          this.userId.set(params['userId']);
          this.userDetailsForm.get('isActive')?.enable();
          this.getUserWithConfigDetails(params['userId']);
        }else{
          this.userDetailsForm.get('isActive')?.disable();
        }
      }
    });
    this.activateRoute.queryParams.subscribe(params => {
      const tabChange = params['tabchange'];
      // Only change the tab if 'tabchange' is present in the query params
      if (tabChange && !this.isCreateUser()) {
        this.selectedTab = 1; // Move to the next tab
      }
    });

    this.initialFormValue = this.userDetailsForm.getRawValue(); // Store the initial form value
    
    // Live input formatting subscriptions for phone and mobile
    this.setupPhoneNumberFormatting();
  }

  // Simple format validator - checks if number can be parsed (format check), not if it's assigned
  phoneFormatValidator(countryCodeControlName: string): ValidatorFn {
    const phoneUtil = this.phoneUtil;
    const getMaxPhoneLength = this.getMaxPhoneLength.bind(this);
    
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      
      // Handle null/undefined/empty - let required validator handle it
      // Check if value is null, undefined, empty string, or contains no digits
      if (!value) {
        // null/undefined - let required validator handle it
        return null;
      }
      
      // Extract phone number string
      let phoneNumberStr = '';
      if (typeof value === 'string') {
        phoneNumberStr = value.trim();
      } else if (typeof value === 'object' && value !== null && value.number !== undefined) {
        phoneNumberStr = String(value.number).trim();
      } else {
        // Unknown type - let required validator handle it
        return null;
      }
      
      // Extract digits only to check if there are any digits
      const digits = phoneNumberStr.replace(/\D/g, '');
      
      // If no digits at all (empty string or only formatting characters), let required validator handle it
      if (digits.length === 0) {
        return null;
      }
      
      // Get country code from form first to determine expected length
      const formGroup = control.parent;
      if (!formGroup) {
        // No form group - do basic validation
        if (digits.length > 15) {
          return { validatePhoneNumber: true };
        }
        return null;
      }
      
      const countryCode = formGroup.get(countryCodeControlName)?.value;
      
      // Determine max length for the country using comprehensive mapping
      const maxLength = countryCode ? getMaxPhoneLength(countryCode) : 15;
      
      // Check length BEFORE parsing - reject if too long
      if (digits.length > maxLength) {
        return { 
          validatePhoneNumber: true,
          maxLength: maxLength,
          actualLength: digits.length,
          errorType: 'maxLength'
        };
      }
      
      if (!countryCode) {
        return null;
      }
      
      // Try to parse the number - if parsing succeeds, format is correct
      try {
        const regionCode = String(countryCode).toLowerCase();
        
        // Parse as national number
        try {
          const parsedNumber = phoneUtil.parse(digits, regionCode);
          
          // Get the parsed national number length
          const nationalNumber = parsedNumber.getNationalNumber();
          const nationalNumberLength = nationalNumber ? String(nationalNumber).length : digits.length;
          
          // Validate parsed length doesn't exceed max length
          if (nationalNumberLength > maxLength) {
            return { 
              validatePhoneNumber: true,
              maxLength: maxLength,
              actualLength: nationalNumberLength,
              errorType: 'maxLength'
            };
          }
          
          // If parsing succeeds and length is within limits, format is correct - accept it
          return null;
        } catch (parseError) {
          // If parsing fails, it might be invalid format or incomplete number
          // Allow incomplete numbers (user might still be typing)
          return null;
        }
      } catch (error) {
        // If error occurs, check max length only
        if (digits.length > maxLength) {
          return { 
            validatePhoneNumber: true,
            maxLength: maxLength,
            actualLength: digits.length,
            errorType: 'maxLength'
          };
        }
        return null;
      }
    };
  }
  setupPhoneNumberFormatting() {
    const phoneCtrl = this.userDetailsForm.get('phone');
    const mobileCtrl = this.userDetailsForm.get('mobileNumber');
    const phoneCountryCodeCtrl = this.userDetailsForm.get('phoneCountryCode');
    const mobileCountryCodeCtrl = this.userDetailsForm.get('mobileNumberCountryCode');
    
    if (phoneCtrl) {
      this.phoneSub = phoneCtrl.valueChanges.subscribe((value: any) => {
        // Skip processing during form submission or binding to prevent value reset
        if (this.isSubmitting || this.isBinding) {
          return;
        }
        
        // Skip processing if value is null/undefined/empty - let required validator handle it
        if (!value || (typeof value === 'string' && value.trim().length === 0)) {
          // Don't interfere with required validation - just return
          return;
        }
        
        if (typeof value === 'string') {
          // Extract digits directly from input (don't use extractRawPhoneNumber for real-time formatting)
          const digits = value.replace(/\D/g, '');
          
          // If no digits, don't process - let required validator handle it
          if (digits.length === 0) {
            return;
          }
          
          const countryCode = this.userDetailsForm.get('phoneCountryCode')?.value || CountryISO.UnitedStates;
          
          // Get country-specific max length using comprehensive mapping
          const maxLength = this.getMaxPhoneLength(countryCode);
          
          // If digits exceed max length, truncate IMMEDIATELY to prevent excessive input
          if (digits.length > maxLength) {
            const truncated = digits.substring(0, maxLength);
            const truncatedFormatted = this.formatPhoneNumber(truncated, countryCode);
            // Set value immediately to prevent further input
            phoneCtrl.setValue(truncatedFormatted || truncated, { emitEvent: false });
            phoneCtrl.updateValueAndValidity({ emitEvent: false });
            // Prevent the current value from being set
            return;
          }
          
          // Format the raw digits directly (consistent extraction for real-time formatting)
          // Don't use extractRawPhoneNumber here as it's meant for already-formatted numbers
          const formatted = this.formatPhoneNumber(digits, countryCode);
          
          // For UK, ALWAYS apply formatting to ensure consistency
          // Use a more aggressive approach to override component's internal formatting
          if (formatted) {
            if (countryCode === CountryISO.UnitedKingdom) {
              // Store the expected formatted value
              const expectedFormatted = formatted;
              
              // Apply formatting immediately
              phoneCtrl.setValue(expectedFormatted, { emitEvent: false });
              
              // Use multiple timing strategies to ensure formatting sticks
              // Strategy 1: requestAnimationFrame (next frame)
              requestAnimationFrame(() => {
                const currentValue = phoneCtrl.value;
                if (currentValue && typeof currentValue === 'string') {
                  const currentDigits = currentValue.replace(/\D/g, '');
                  if (currentDigits === digits && currentValue !== expectedFormatted) {
                    phoneCtrl.setValue(expectedFormatted, { emitEvent: false });
                  }
                }
              });
              
              // Strategy 2: setTimeout with short delay
              setTimeout(() => {
                const currentValue = phoneCtrl.value;
                if (currentValue && typeof currentValue === 'string') {
                  const currentDigits = currentValue.replace(/\D/g, '');
                  if (currentDigits === digits && currentValue !== expectedFormatted) {
                    phoneCtrl.setValue(expectedFormatted, { emitEvent: false });
                    this.cdr.detectChanges();
                  }
                }
              }, 50);
              
              // Strategy 3: setTimeout with longer delay as final fallback
              setTimeout(() => {
                const currentValue = phoneCtrl.value;
                if (currentValue && typeof currentValue === 'string') {
                  const currentDigits = currentValue.replace(/\D/g, '');
                  if (currentDigits === digits && currentValue !== expectedFormatted) {
                    phoneCtrl.setValue(expectedFormatted, { emitEvent: false });
                    this.cdr.detectChanges();
                  }
                }
              }, 200);
            } else if (formatted !== value) {
              // For other countries, only format if different
              phoneCtrl.setValue(formatted, { emitEvent: false });
            }
          }
          
          // Mark as touched and dirty immediately so errors show while typing
          phoneCtrl.markAsTouched();
          phoneCtrl.markAsDirty();
          
          // Trigger validation immediately - validate the current value
          phoneCtrl.updateValueAndValidity({ emitEvent: false });
        } else if (value && typeof value === 'object' && value.number !== undefined) {
            const countryCode = value.countryCode || this.userDetailsForm.get('phoneCountryCode')?.value || CountryISO.UnitedStates;
            if (value.countryCode) {
              this.userDetailsForm.get('phoneCountryCode')?.setValue(value.countryCode, { emitEvent: false });
            }
            const rawNumber = this.extractRawPhoneNumber(value.number, countryCode);
            const formatted = this.formatPhoneNumber(rawNumber, countryCode);
            phoneCtrl.setValue(formatted || '', { emitEvent: false });
            // Trigger validation after formatting
            setTimeout(() => {
              phoneCtrl.updateValueAndValidity({ emitEvent: false });
            }, 0);
        }
      });
    }
    
    if (mobileCtrl) {
      this.mobileSub = mobileCtrl.valueChanges.subscribe((value: any) => {
        // Skip processing during form submission or binding to prevent value reset
        if (this.isSubmitting || this.isBinding) {
          return;
        }
        
        // Skip processing if value is null/undefined/empty - let required validator handle it
        if (!value || (typeof value === 'string' && value.trim().length === 0)) {
          // Don't interfere with required validation - just return
          return;
        }
        
        if (typeof value === 'string') {
          // Extract digits directly from input (don't use extractRawPhoneNumber for real-time formatting)
          const digits = value.replace(/\D/g, '');
          
          // If no digits, don't process - let required validator handle it
          if (digits.length === 0) {
            return;
          }
          
          const countryCode = this.userDetailsForm.get('mobileNumberCountryCode')?.value || CountryISO.UnitedStates;
          
          // Get country-specific max length using comprehensive mapping
          const maxLength = this.getMaxPhoneLength(countryCode);
          
          // If digits exceed max length, truncate IMMEDIATELY to prevent excessive input
          if (digits.length > maxLength) {
            const truncated = digits.substring(0, maxLength);
            const truncatedFormatted = this.formatPhoneNumber(truncated, countryCode);
            // Set value immediately to prevent further input
            mobileCtrl.setValue(truncatedFormatted || truncated, { emitEvent: false });
            mobileCtrl.updateValueAndValidity({ emitEvent: false });
            // Prevent the current value from being set
            return;
          }
          
          // Format the raw digits directly (consistent extraction for real-time formatting)
          // Don't use extractRawPhoneNumber here as it's meant for already-formatted numbers
          const formatted = this.formatPhoneNumber(digits, countryCode);
          
          // For UK, ALWAYS apply formatting to ensure consistency
          // Use a more aggressive approach to override component's internal formatting
          if (formatted) {
            if (countryCode === CountryISO.UnitedKingdom) {
              // Store the expected formatted value
              const expectedFormatted = formatted;
              
              // Apply formatting immediately
              mobileCtrl.setValue(expectedFormatted, { emitEvent: false });
              
              // Use multiple timing strategies to ensure formatting sticks
              // Strategy 1: requestAnimationFrame (next frame)
              requestAnimationFrame(() => {
                const currentValue = mobileCtrl.value;
                if (currentValue && typeof currentValue === 'string') {
                  const currentDigits = currentValue.replace(/\D/g, '');
                  if (currentDigits === digits && currentValue !== expectedFormatted) {
                    mobileCtrl.setValue(expectedFormatted, { emitEvent: false });
                  }
                }
              });
              
              // Strategy 2: setTimeout with short delay
              setTimeout(() => {
                const currentValue = mobileCtrl.value;
                if (currentValue && typeof currentValue === 'string') {
                  const currentDigits = currentValue.replace(/\D/g, '');
                  if (currentDigits === digits && currentValue !== expectedFormatted) {
                    mobileCtrl.setValue(expectedFormatted, { emitEvent: false });
                    this.cdr.detectChanges();
                  }
                }
              }, 50);
              
              // Strategy 3: setTimeout with longer delay as final fallback
              setTimeout(() => {
                const currentValue = mobileCtrl.value;
                if (currentValue && typeof currentValue === 'string') {
                  const currentDigits = currentValue.replace(/\D/g, '');
                  if (currentDigits === digits && currentValue !== expectedFormatted) {
                    mobileCtrl.setValue(expectedFormatted, { emitEvent: false });
                    this.cdr.detectChanges();
                  }
                }
              }, 200);
            } else if (formatted !== value) {
              // For other countries, only format if different
              mobileCtrl.setValue(formatted, { emitEvent: false });
            }
          }
          
          // Mark as touched and dirty immediately so errors show while typing
          mobileCtrl.markAsTouched();
          mobileCtrl.markAsDirty();
          
          // Trigger validation immediately - validate the current value
          mobileCtrl.updateValueAndValidity({ emitEvent: false });
        } else if (value && typeof value === 'object' && value.number !== undefined) {
            const countryCode = value.countryCode || this.userDetailsForm.get('mobileNumberCountryCode')?.value || CountryISO.UnitedStates;
            if (value.countryCode) {
              this.userDetailsForm.get('mobileNumberCountryCode')?.setValue(value.countryCode, { emitEvent: false });
            }
            const rawNumber = this.extractRawPhoneNumber(value.number, countryCode);
            const formatted = this.formatPhoneNumber(rawNumber, countryCode);
            mobileCtrl.setValue(formatted || '', { emitEvent: false });
            // Trigger validation after formatting
            setTimeout(() => {
              mobileCtrl.updateValueAndValidity({ emitEvent: false });
            }, 0);
        }
      });
    }
    
    // Subscribe to country code changes to reset phone numbers and trigger validation
    if (phoneCountryCodeCtrl) {
      this.phoneCountryCodeSub = phoneCountryCodeCtrl.valueChanges.subscribe((countryCode: CountryISO | null) => {
        if (countryCode) {
          // Reset phone number field when country code changes
          phoneCtrl?.setValue('', { emitEvent: false });
        }
        // Trigger validation when country code changes (after a small delay to ensure value is set)
        if (phoneCtrl) {
          setTimeout(() => {
            phoneCtrl.markAsTouched();
            phoneCtrl.markAsDirty();
            phoneCtrl.updateValueAndValidity();
          }, 100);
        }
      });
    }
    
    if (mobileCountryCodeCtrl) {
      this.mobileCountryCodeSub = mobileCountryCodeCtrl.valueChanges.subscribe((countryCode: CountryISO | null) => {
        if (countryCode) {
          // Reset phone number field when country code changes
          mobileCtrl?.setValue('', { emitEvent: false });
        }
        // Trigger validation when country code changes (after a small delay to ensure value is set)
        if (mobileCtrl) {
          setTimeout(() => {
            mobileCtrl.markAsTouched();
            mobileCtrl.markAsDirty();
            mobileCtrl.updateValueAndValidity();
          }, 100);
        }
      });
    }
  }

  getCountryList() {
    this.systemAdminService.getCountryList().subscribe({
      next: (response: any) => {
        if(response){
          this.countryList.set(response);
          // If country list is loaded and there's already a branch selected, set countryCode
          // This will be handled by user-details component, but we ensure countryList is available
        }
      },
      error: (error: any) => {
        console.log("error", error);
        this.toastService.addNotification({message: 'System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.', type: ToastType.Error});
      }
    })
  }

  getUserDetails(userId: string) {
    this.systemAdminService.getUserDetails(userId).subscribe({
      next:(response: UserDetailsResponse) => {
        if(response){
          this.userTitle.set(response?.userName);
          this.onBindUserDetailsFormData(response);
        }
      },
      error:(error: any) => {
        console.log("error", error);
        this.toastService.addNotification({message: 'System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.', type: ToastType.Error});
      }
    });
  }

  onAdminCompanySelected(isAdmin: boolean) {
    this.isAdminCompanySelected.set(isAdmin);    
}

  getUserWithConfigDetails(userId: string) {
    forkJoin([
      this.systemAdminService.getAllCompanyList(),
      this.systemAdminService.getUserDetails(userId),
      this.systemAdminService.getUserConfig(userId),
    ]).pipe(
        map(([companyList,userDetails, configDetails]) => {
            return {companyList,userDetails, configDetails};
        })
    ).subscribe({
      next: (response: any) => {
        if(response){
          const {companyList,userDetails, configDetails} = response;
          this.userTitle.set(userDetails?.userName);
          this.companyList.set(companyList);
          this.onBindUserDetailsFormData(userDetails);
          this.onBindUserConfigFormData(configDetails);
          this.changesSaved = true;
        }
      },
      error: (error: any) => {
        console.log("error", error);
        this.toastService.addNotification({message: 'System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.', type: ToastType.Error});
      }
    })
  }

  onBindUserDetailsFormData(formValue: any) {
    // Set binding flag to prevent valueChanges from interfering
    this.isBinding = true;
    
    // Convert phone numbers to strings if they're numbers
    const phoneStr = formValue?.phone !== null && formValue?.phone !== undefined ? String(formValue.phone) : null;
    const mobileNumberStr = formValue?.mobileNumber !== null && formValue?.mobileNumber !== undefined ? String(formValue.mobileNumber) : null;
    
    this.userDetailsForm.patchValue({
      firstName: formValue?.firstName,
      lastName: formValue?.lastName,
      userName: formValue?.userName,
      mobileNumber: mobileNumberStr,
      companyId: formValue?.companyId,
      branchId: formValue?.branchId,
      userRoleId: formValue?.userRoleId,
      userEmail: formValue?.userEmail,  
      phone: phoneStr,
      iPhoneExt: formValue?.iPhoneExt,
      userDetailId: formValue?.userDetailId,
      isActive: formValue?.isActive
    });
     this.systemAdminService.setSysUserEmailID(formValue?.userEmail); // set userEmailID
    
    // Get country codes from response
    // NOTE: Field names are confusing - MobilePhoneCode contains Phone Number's ISO2, PhoneCode contains Mobile Number's ISO2
    // For Phone Number: MobilePhoneCode (ISO2) or countryCode (dial code)
    // For Mobile Number: PhoneCode (ISO2) or mobileCountryCode (dial code)
    const phoneISO2FromResponse = formValue?.MobilePhoneCode ?? formValue?.mobilePhoneCode ?? null;
    const phoneDialCodeFromResponse = formValue?.countryCode ?? null;
    const mobileISO2FromResponse = formValue?.PhoneCode ?? formValue?.phoneCode ?? null;
    const mobileDialCodeFromResponse = formValue?.mobileCountryCode ?? null;
    
    // Check if phone codes are missing (for existing records)
    const phoneCodesMissing = !phoneISO2FromResponse && !phoneDialCodeFromResponse && !mobileISO2FromResponse && !mobileDialCodeFromResponse;
    
    // Prioritize ISO2 values, fallback to converting dial codes to ISO2
    let phoneCountryCodeISO: CountryISO | null = null;
    if (phoneISO2FromResponse) {
      // ISO2 is already in the correct format (e.g., "AL") - ensure uppercase
      phoneCountryCodeISO = phoneISO2FromResponse.toUpperCase() as CountryISO;
    } else if (phoneDialCodeFromResponse) {
      // Convert dial code to ISO2
      phoneCountryCodeISO = this.mapCallingCodeToISO(phoneDialCodeFromResponse);
    }
    
    let mobileCountryCodeISO: CountryISO | null = null;
    if (mobileISO2FromResponse) {
      // ISO2 is already in the correct format (e.g., "AF") - ensure uppercase
      mobileCountryCodeISO = mobileISO2FromResponse.toUpperCase() as CountryISO;
    } else if (mobileDialCodeFromResponse) {
      // Convert dial code to ISO2
      mobileCountryCodeISO = this.mapCallingCodeToISO(mobileDialCodeFromResponse);
    }
    
    // If phone codes are missing and branchId exists, fetch branch details to get country ID
    if (phoneCodesMissing && formValue?.branchId) {
      this.fetchBranchDetailsForPhoneCodes(formValue.branchId, phoneStr, mobileNumberStr);
      return; // Exit early - phone codes will be set in fetchBranchDetailsForPhoneCodes
    }
    
    const phoneCountryCode = phoneCountryCodeISO || CountryISO.UnitedStates;
    const mobileCountryCode = mobileCountryCodeISO || CountryISO.UnitedStates;
    
    // Set phone country codes FIRST (before formatting phone numbers)
    // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.userDetailsForm.get('phoneCountryCode')?.setValue(phoneCountryCode, { emitEvent: false });
      this.userDetailsForm.get('mobileNumberCountryCode')?.setValue(mobileCountryCode, { emitEvent: false });
      
      // Set countryCode in form - use phone dial code (for backward compatibility)
      if (phoneDialCodeFromResponse) {
        this.userDetailsForm.get('countryCode')?.setValue(String(phoneDialCodeFromResponse));
      }
      
      // Format phone numbers AFTER country codes are set
      // Use longer delay to ensure country codes are set in the form first
      setTimeout(() => {
        // Double-check that country codes are set in form before formatting
        const currentPhoneCode = this.userDetailsForm.get('phoneCountryCode')?.value || phoneCountryCode;
        const currentMobileCode = this.userDetailsForm.get('mobileNumberCountryCode')?.value || mobileCountryCode;
        
        // Format with explicit country codes to ensure correct formatting
        this.formatPhoneNumbersForBinding(phoneStr, mobileNumberStr, currentPhoneCode, currentMobileCode);
        
        // Re-set country codes after a delay to ensure ngx-intl-tel-input initializes correctly
        // This is important when ISO2 codes are used (e.g., "AI" vs "AG" both having "+1")
        setTimeout(() => {
          if (phoneCountryCodeISO) {
            this.userDetailsForm.get('phoneCountryCode')?.setValue(phoneCountryCodeISO, { emitEvent: false });
          }
          if (mobileCountryCodeISO) {
            this.userDetailsForm.get('mobileNumberCountryCode')?.setValue(mobileCountryCodeISO, { emitEvent: false });
          }
          this.cdr.detectChanges();
          // Clear binding flag after formatting is complete
          this.isBinding = false;
        }, 100);
      }, 150);
    }, 0);
    
   
    
    // Format phone numbers after binding
    const selectedCompany = this.companyList().find(company => company.companyId == formValue?.companyId);
        if (selectedCompany) {
            const companyTypeId = selectedCompany.companyType;
            const companyTypes = this.systemAdminService.getCompanyTypeList(); // Fetch from local storage

            if (companyTypes[companyTypeId] && companyTypes[companyTypeId].code === "Admin Company") {            
                this.isAdminCompanySelected.set(true);
                
            } else {               
                this.isAdminCompanySelected.set(false);
                
            }
        }
  }

  getCompanyList() {
    this.systemAdminService.getAllCompanyList().subscribe({
        next: (response:any) => {
            if(response && response?.length > 0){
                this.companyList.set(response)
            }
        },
        error: (error: any) => {
            console.log("error", error);
        } 
    })
}

  onBindUserConfigFormData(formValue: any) {
    this.userConfigForm.patchValue({
      isEmailverified: formValue?.isEmailverified ? formValue?.isEmailverified : 0,
      isQrValidated: formValue?.isQrValidated ? formValue?.isQrValidated : 0,
      isResetVerified: formValue?.isResetVerified ? formValue?.isResetVerified : 0,
      isLocked: formValue?.isLocked ? formValue?.isLocked : 0
    });
  }

  onTabChange(index: number): void {
    this.selectedTab = index;
    this.isTabChange.set(true);
  }

  onCreateNewUser(userID: string){
    this.isCreateUser.set(false);
    this.changesSaved = true;
    this.router.navigate(['/system-admin-dashboard/user/edit-user', userID],
      { queryParams: { tabchange: true } }
    );
    this.userId.set(userID);
    this.userDetailsForm.get('isActive')?.enable();
    this.getUserWithConfigDetails(userID);
  }

  saveUserDetails(payload: UserDetailsRequestPayload) {
    this.systemAdminService.saveUserDetails(payload).subscribe({
        next: (response: any) => {
            if(response){
              // if response is success then prompt
              const msg =  response?.message || "User details added successfully";
              this.toastService.addNotification({message: msg, type: ToastType.Success}); // success notification
              this.changesSaved = true;
              this.onCreateNewUser(String(response?.data?.userId)); // create user & move to product detail tab
            }
        },
        error: (error: any) => {
          console.log("error", error);
          if (error.status === 400) {
            const msg = error?.error?.message || "System error. Failed to save data. Please exit and contact help@AbacusAU.com.";
            this.toastService.addNotification({message: msg, type: ToastType.Error}); // error notification
          }else{
            this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
          }
        }
    })
  }

  updateUserDetails(payload: UserDetailsRequestPayload, isEmailUpdate: boolean = false) {
    this.systemAdminService.updateUserDetails(payload).subscribe({
        next: (response: any) => {
            if(response){
              // if response is success then prompt
              const msg =  response?.message || "User details updated successfully";
              this.toastService.addNotification({message: msg, type: ToastType.Success}); // success notification
              this.changesSaved = true;
              if(isEmailUpdate){
                this.onUpdateEmailAddress(); 
              }else{
                this.router.navigate(['/system-admin-dashboard/user']);
              }
            }
        },
        error: (error: any) => {
          console.log("error", error);
          if (error.status === 400) {
            const msg = error?.error?.message || "System error. Failed to save data. Please exit and contact help@AbacusAU.com.";
            this.toastService.addNotification({message: msg, type: ToastType.Error}); // error notification
          }else{
            this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
          }
        }
    });
  }

  updateUserConfigDetails(payload: UserConfigRequestPayload) {
    this.systemAdminService.updateUserConfig(payload, this.userId()).subscribe({
      next: (response: any) => {
        if(response){
          this.onBindUserConfigFormData(response);
          this.changesSaved = true;
          // if response is success then prompt
          const msg =  response?.message || "User config updated successfully";
          this.toastService.addNotification({message: msg, type: ToastType.Success}); // success notification
          this.router.navigate(['/system-admin-dashboard/user']);
        }
      },
      error: (error: any) => {
        console.log("error", error);
        this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
      }
    })
  }

onCancel(action: boolean): void{
  if(action) {
    const dialog = this.dialog.open(ConfirmationDialogComponent, {
      data: { message: "Unsaved changes will be lost if you navigate from this page. Do you want to continue?" }
    });

    dialog.afterClosed().subscribe((result: boolean) => {
      if(result){
        this.userDetailsForm.reset();
        this.router.navigate(['/system-admin-dashboard/user']);
      }
    })
  };
}

onUserEmailChange(action: boolean): void {
  if(action){
    const dialog = this.dialog.open(EmailChangeConfirmation, { width:'auto', minWidth:"600px"});

    dialog.afterClosed().subscribe((result: boolean) => {
      if(result){
        this.onUserDetailsSubmit(action, true);   // update email address
      }
    });
  }
}

onUpdateEmailAddress() {
  this.systemAdminService.updateEmailAddress(this.userId(), String(CreatePasswordIsReset.USER_EMAIL_UPDATE)).subscribe({
    next: (response: any) => {
      if(response){
        this.router.navigate(['/system-admin-dashboard/user']);
      }
    },
    error: (error: any) => {
      console.log("error", error);
      this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
    }
  });
}

onSetupLinkAction(action: boolean): void {
  if(action){
    this.systemAdminService.updateEmailAddress(this.userId(), String(CreatePasswordIsReset.PASSWORD_LINK)).subscribe({
      next: (response: any) => {
        if(response){
          this.toastService.addNotification({message: "Data saved successfully", type: ToastType.Success}); // success notification
          this.router.navigate(['/system-admin-dashboard/user']); // redirect to user landing page
        }
      },
      error: (error: any) => {
        console.log("error", error);
        this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
      }
    });
  }
}

onResetLinkAction(action: boolean): void {
  if(action){
    const getSysUserEmail = sessionStorage.getItem('sysEmailID'); 
    this.authService.forgotPassword(String(getSysUserEmail).toLowerCase()).subscribe({
      next: (response: any) => {
        if(response){
          this.toastService.addNotification({message: "Data saved successfully", type: ToastType.Success}); // success notification
          this.router.navigate(['/system-admin-dashboard/user']); // redirect to user landing page
        }
      },
      error: (error: any) => {
        console.log("error", error);
        this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
      }
    });
  }
}

onSaveHandler(action: boolean){
  if(this.isCreateUser()){
    this.onUserDetailsSubmit(action);
  }else{
    const getSysUserEmail = sessionStorage.getItem('sysEmailID'); 
    if(String(getSysUserEmail).toLowerCase().trim() !== String(this.userDetailsForm.get('userEmail')?.value).toLowerCase().trim()){
      this.onUserEmailChange(action); // Confirmation popup
    }else{
      this.onUserDetailsSubmit(action);
    }
  } 
}

  onUserDetailsSubmit(action: boolean, isEmailUpdate: boolean = false) {
  // Set submitting flag to prevent valueChanges subscriptions from interfering
  this.isSubmitting = true;
  
  try {
    // Mark all fields as touched to show validation errors
    Object.keys(this.userDetailsForm.controls).forEach(key => {
      const control = this.userDetailsForm.get(key);
      if (control) {
        control.markAsTouched();
      }
    });
    
    // Explicitly check for empty phone and mobile number BEFORE calling updateValueAndValidity
    const phoneValue = this.userDetailsForm.get('phone')?.value;
    const mobileValue = this.userDetailsForm.get('mobileNumber')?.value;
    
    // Check if phone is empty (null, undefined, empty string, or only whitespace/formatting)
    const phoneDigits = this.extractDigitsFromTelValue(phoneValue);
    const mobileDigits = this.extractDigitsFromTelValue(mobileValue);
    
    // Now update validity for all controls - this will trigger validators
    Object.keys(this.userDetailsForm.controls).forEach(key => {
      const control = this.userDetailsForm.get(key);
      if (control) {
        control.updateValueAndValidity({ emitEvent: false });
      }
    });
    
    // After validation, explicitly set required errors if still empty (to ensure they persist)
    if (phoneDigits.length === 0) {
      const phoneCtrl = this.userDetailsForm.get('phone');
      if (phoneCtrl) {
        const currentErrors = phoneCtrl.errors || {};
        phoneCtrl.setErrors({ ...currentErrors, required: true }, { emitEvent: false });
        phoneCtrl.markAsTouched();
      }
    }
    
    if (mobileDigits.length === 0) {
      const mobileCtrl = this.userDetailsForm.get('mobileNumber');
      if (mobileCtrl) {
        const currentErrors = mobileCtrl.errors || {};
        mobileCtrl.setErrors({ ...currentErrors, required: true }, { emitEvent: false });
        mobileCtrl.markAsTouched();
      }
    }
    
    if(this.userDetailsForm.invalid){
      this.isSubmitting = false;
      return;
    }

  if(action) {
    const { firstName, lastName, userName, companyId, branchId, userRoleId, userEmail, phone, iPhoneExt,IPhone, mobileNumber, isActive, userDetailId } = this.userDetailsForm.getRawValue();

    // Extract raw phone numbers (remove country code prefix and formatting)
    const cleanedPhone = this.extractRawPhoneNumber(phone);
    const cleanedMobileNumber = this.extractRawPhoneNumber(mobileNumber);

    // Get country codes from dropdowns
    const phoneCountryCodeISO = this.userDetailsForm.get('phoneCountryCode')?.value as CountryISO | null;
    const mobileCountryCodeISO = this.userDetailsForm.get('mobileNumberCountryCode')?.value as CountryISO | null;
    
    // Convert CountryISO to ISO2 string (e.g., CountryISO.Albania -> "AL")
    const phoneISO2 = phoneCountryCodeISO ? String(phoneCountryCodeISO) : null;
    const mobileISO2 = mobileCountryCodeISO ? String(mobileCountryCodeISO) : null;
    
    // Convert CountryISO to calling codes (e.g., CountryISO.Albania -> "+355")
    const phoneCallingCode = phoneCountryCodeISO ? this.getCountryCallingCode(phoneCountryCodeISO) : null;
    const mobileCallingCode = mobileCountryCodeISO ? this.getCountryCallingCode(mobileCountryCodeISO) : null;

    const payload: UserDetailsRequestPayload = {
      firstName, lastName, companyId, branchId, userRoleId, userEmail: String(userEmail).toLowerCase().trim(), 
      phone: cleanedPhone ? String(cleanedPhone) : null, // Phone number as string
      iPhoneExt, IPhone, 
      mobileNumber: cleanedMobileNumber ? String(cleanedMobileNumber) : null, // Mobile number as string
      countryCode: phoneCallingCode, // Phone number dial code (e.g., "+355")
      MobilePhoneCode: phoneISO2, // Phone number ISO2 (e.g., "AL")
      PhoneCode: mobileISO2, // Mobile number ISO2 (e.g., "AF")
      mobileCountryCode: mobileCallingCode, // Mobile number dial code (e.g., "+93")
      isActive, createdUserId: Number(this.authService.getUserDetailsFromStorage()?.createdUserId), updatedUserId: Number(this.authService.getUserDetailsFromStorage()?.updatedUserId),
      userName, userTypeId: userRoleId, createdDate: new Date().toISOString(),
    }

    if(this.isCreateUser()){
      this.saveUserDetails(payload);
    }else{
      payload.userDetailId = userDetailId;
      payload.userId = Number(this.userId());
      this.updateUserDetails(payload, isEmailUpdate);
    }
    }
  } finally {
    // Reset submitting flag after submission attempt
    this.isSubmitting = false;
  }
}

extractRawPhoneNumber(
  formattedPhoneNumber: string | number | { number?: string | number | null } | null | undefined,
  countryCode?: CountryISO | null
): string | null {
  // ngx-intl-tel-input can emit object values; extract the underlying number
  if (formattedPhoneNumber && typeof formattedPhoneNumber === 'object') {
    formattedPhoneNumber = formattedPhoneNumber.number ?? null;
  }

  if (typeof formattedPhoneNumber !== 'string' && typeof formattedPhoneNumber !== 'number') {
    return null;
  }
  
  // Convert to string if number
  const phoneStr = typeof formattedPhoneNumber === 'number' ? formattedPhoneNumber.toString() : formattedPhoneNumber;
  
  if (!phoneStr) {
    return null;
  }
  
  // Extract all digits first
  let cleaned = phoneStr.replace(/\D/g, '');
  
  if (!cleaned || cleaned.length === 0) {
    return null;
  }
  
  // Use provided countryCode or try to get from form fields
  const phoneCountryCode = this.userDetailsForm.get('phoneCountryCode')?.value;
  const mobileCountryCode = this.userDetailsForm.get('mobileNumberCountryCode')?.value;
  const selectedCountryCode = countryCode || phoneCountryCode || mobileCountryCode;
  
  if (!selectedCountryCode) {
    // No country code - return cleaned digits as-is
    return cleaned;
  }

  // Try to parse and extract national number
  try {
    const regionCode = String(selectedCountryCode).toLowerCase();
    const parsedNumber = this.phoneUtil.parse(cleaned, regionCode);
    
    // Get national number (without country code)
    const nationalNumber = parsedNumber.getNationalNumber();
    if (nationalNumber) {
      return String(nationalNumber);
    }
  } catch (error) {
    // Parsing failed - might be incomplete number, try to remove country code prefix manually
  }
  
  // Fallback: remove country code prefix if present
  const countryCodePrefix = this.getCountryCodePrefix(selectedCountryCode);
  if (countryCodePrefix) {
    const prefixDigits = countryCodePrefix.replace(/\D/g, '');
    // Check if the cleaned number starts with the country code prefix
    if (cleaned.startsWith(prefixDigits)) {
      // Remove the prefix to get national number
      cleaned = cleaned.substring(prefixDigits.length);
      // For UK (44), also remove leading 0 if present (UK national format starts with 0)
      if (selectedCountryCode === CountryISO.UnitedKingdom && cleaned.startsWith('0')) {
        cleaned = cleaned.substring(1);
      }
    }
  }
  
  return cleaned;
}

  // Get min length based on country code - comprehensive mapping
  getMinPhoneLength(countryCode: CountryISO | null | undefined): number {
    if (!countryCode) return 7;
    const regionCode = String(countryCode).toLowerCase();
    
    // Comprehensive country-to-min-length mapping
    // For most countries, min = max (fixed length), but some have ranges
    const countryMinLengths: { [key: string]: number } = {
      // Very short numbers (4-5 digits)
      'nu': 4,  // Niue
      'tk': 4,  // Tokelau
      'nf': 5,  // Norfolk Island
      
      // Short numbers (6-7 digits)
      'gi': 8,  // Gibraltar
      'va': 8,  // Vatican City
      'sm': 9,  // San Marino
      'mc': 9,  // Monaco
      'li': 9,  // Liechtenstein
      
      // Standard 10-digit numbers (US, Canada, etc.)
      'us': 10, // United States
      'ca': 10, // Canada
      'ru': 10, // Russia
      'kz': 10, // Kazakhstan
      'mx': 10, // Mexico
      'br': 10, // Brazil (can be 10 or 11)
      'in': 10, // India
      'au': 9,  // Australia
      'nz': 9,  // New Zealand
      'za': 9,  // South Africa
      'ar': 10, // Argentina
      'cl': 9,  // Chile
      'co': 10, // Colombia
      'pe': 9,  // Peru
      've': 10, // Venezuela
      'ec': 9,  // Ecuador
      'uy': 8,  // Uruguay
      'py': 9,  // Paraguay
      'bo': 8,  // Bolivia
      'cr': 8,  // Costa Rica
      'pa': 8,  // Panama
      'gt': 8,  // Guatemala
      'hn': 8,  // Honduras
      'ni': 8,  // Nicaragua
      'sv': 8,  // El Salvador
      'do': 10, // Dominican Republic
      'cu': 8,  // Cuba
      'jm': 10, // Jamaica
      'tt': 10, // Trinidad and Tobago
      'bb': 10, // Barbados
      'bs': 10, // Bahamas
      'bz': 7,  // Belize
      'gy': 7,  // Guyana
      'sr': 7,  // Suriname
      'gf': 9,  // French Guiana
      
      // 11-digit numbers (UK, etc.)
      'gb': 10, // United Kingdom (10-11 digits)
      'ie': 9,  // Ireland
      'nl': 9,  // Netherlands
      'be': 9,  // Belgium
      'lu': 9,  // Luxembourg
      'ch': 9,  // Switzerland
      'at': 10, // Austria
      'de': 10, // Germany (10-11 digits)
      'dk': 8,  // Denmark
      'se': 9,  // Sweden
      'no': 8,  // Norway
      'fi': 9,  // Finland
      'is': 7,  // Iceland
      'pl': 9,  // Poland
      'cz': 9,  // Czech Republic
      'sk': 9,  // Slovakia
      'hu': 9,  // Hungary
      'ro': 10, // Romania
      'bg': 9,  // Bulgaria
      'gr': 10, // Greece
      'pt': 9,  // Portugal
      'es': 9,  // Spain
      'it': 10, // Italy
      'mt': 8,  // Malta
      'cy': 8,  // Cyprus
      
      // Eastern Europe
      'ua': 9,  // Ukraine
      'by': 9,  // Belarus
      'lt': 8,  // Lithuania
      'lv': 8,  // Latvia
      'ee': 8,  // Estonia
      'md': 8,  // Moldova
      'rs': 9,  // Serbia
      'hr': 9,  // Croatia
      'si': 8,  // Slovenia
      'ba': 8,  // Bosnia and Herzegovina
      'mk': 9,  // North Macedonia
      'al': 9,  // Albania
      'me': 8,  // Montenegro
      'xk': 8,  // Kosovo
      
      // Middle East
      'sa': 9,  // Saudi Arabia
      'ae': 9,  // UAE
      'kw': 8,  // Kuwait
      'qa': 8,  // Qatar
      'bh': 8,  // Bahrain
      'om': 8,  // Oman
      'ye': 9,  // Yemen
      'iq': 10, // Iraq
      'ir': 10, // Iran
      'il': 9,  // Israel
      'jo': 9,  // Jordan
      'lb': 8,  // Lebanon
      'sy': 9,  // Syria
      'ps': 9,  // Palestine
      'tr': 10, // Turkey
      'eg': 10, // Egypt
      'ly': 9,  // Libya
      'tn': 8,  // Tunisia
      'dz': 9,  // Algeria
      'ma': 9,  // Morocco
      'sd': 9,  // Sudan
      'et': 9,  // Ethiopia
      
      // Asia
      'cn': 11, // China
      'jp': 10, // Japan
      'kr': 10, // South Korea
      'tw': 9,  // Taiwan
      'hk': 8,  // Hong Kong
      'mo': 8,  // Macau
      'sg': 8,  // Singapore
      'my': 9,  // Malaysia
      'th': 9,  // Thailand
      'vn': 10, // Vietnam
      'ph': 10, // Philippines
      'id': 9,  // Indonesia
      'mm': 9,  // Myanmar
      'kh': 9,  // Cambodia
      'la': 8,  // Laos
      'bn': 7,  // Brunei
      'bd': 10, // Bangladesh
      'pk': 10, // Pakistan
      'lk': 9,  // Sri Lanka
      'np': 10, // Nepal
      'af': 7,  // Afghanistan
      'uz': 9,  // Uzbekistan
      'tm': 8,  // Turkmenistan
      'tj': 9,  // Tajikistan
      'kg': 9,  // Kyrgyzstan
      'mn': 8,  // Mongolia
      
      // Oceania
      'pg': 8,  // Papua New Guinea
      'fj': 7,  // Fiji
      'nc': 6,  // New Caledonia
      'pf': 6,  // French Polynesia
      'ws': 7,  // Samoa
      'to': 7,  // Tonga
      'vu': 7,  // Vanuatu
      'sb': 7,  // Solomon Islands
      'ki': 5,  // Kiribati
      'tv': 6,  // Tuvalu
      'nr': 7,  // Nauru
      'pw': 7,  // Palau
      'fm': 7,  // Micronesia
      'mh': 7,  // Marshall Islands
      
      // Africa
      'ng': 10, // Nigeria
      'ke': 9,  // Kenya
      'tz': 9,  // Tanzania
      'ug': 9,  // Uganda
      'rw': 9,  // Rwanda
      'gh': 9,  // Ghana
      'ci': 8,  // Côte d'Ivoire
      'sn': 9,  // Senegal
      'ml': 8,  // Mali
      'bf': 8,  // Burkina Faso
      'ne': 8,  // Niger
      'td': 8,  // Chad
      'cm': 9,  // Cameroon
      'ga': 8,  // Gabon
      'cg': 9,  // Republic of the Congo
      'cd': 9,  // DR Congo
      'ao': 9,  // Angola
      'zm': 9,  // Zambia
      'zw': 9,  // Zimbabwe
      'bw': 8,  // Botswana
      'na': 9,  // Namibia
      'mw': 9,  // Malawi
      'mz': 9,  // Mozambique
      'mg': 9,  // Madagascar
      'mu': 8,  // Mauritius
      'sc': 7,  // Seychelles
      'km': 7,  // Comoros
      'dj': 8,  // Djibouti
      'er': 7,  // Eritrea
      'so': 8,  // Somalia
      'ss': 9,  // South Sudan
      
      // Caribbean
      'ht': 8,  // Haiti
      'pr': 10, // Puerto Rico
      'vi': 10, // US Virgin Islands
      'vg': 10, // British Virgin Islands
      'ky': 7,  // Cayman Islands
      'bm': 10, // Bermuda
      'tc': 7,  // Turks and Caicos
      'ai': 7,  // Anguilla
      'ms': 7,  // Montserrat
      'gd': 7,  // Grenada
      'lc': 7,  // Saint Lucia
      'vc': 7,  // Saint Vincent
      'ag': 10,  // Antigua and Barbuda
      'dm': 7,  // Dominica
      'kn': 7,  // Saint Kitts and Nevis
      
      // Other territories
      'fo': 6,  // Faroe Islands
      'gl': 6,  // Greenland
      'sj': 8,  // Svalbard
      'ax': 6,  // Åland Islands
      'gg': 10, // Guernsey
      'je': 10, // Jersey
      'im': 10, // Isle of Man
      'ad': 6,  // Andorra
    };
    
    return countryMinLengths[regionCode] || 7; // Default to 7 for unknown countries
  }

  // Get max length based on country code - comprehensive mapping
  getMaxPhoneLength(countryCode: CountryISO | null | undefined): number {
    if (!countryCode) return 15;
    const regionCode = String(countryCode).toLowerCase();
    
    // Comprehensive country-to-max-length mapping
    const countryMaxLengths: { [key: string]: number } = {
      // Very short numbers (4-5 digits)
      'nu': 4,  // Niue
      'tk': 4,  // Tokelau
      'nf': 5,  // Norfolk Island
      
      // Short numbers (6-7 digits)
      'gi': 8,  // Gibraltar
      'va': 8,  // Vatican City
      'sm': 9,  // San Marino
      'mc': 9,  // Monaco
      'li': 9,  // Liechtenstein
      
      // Standard 10-digit numbers (US, Canada, etc.)
      'us': 10, // United States
      'ca': 10, // Canada
      'ru': 10, // Russia
      'kz': 10, // Kazakhstan
      'mx': 10, // Mexico
      'br': 11, // Brazil (can be 10 or 11)
      'in': 10, // India
      'au': 9,  // Australia
      'nz': 9,  // New Zealand
      'za': 9,  // South Africa
      'ar': 10, // Argentina
      'cl': 9,  // Chile
      'co': 10, // Colombia
      'pe': 9,  // Peru
      've': 10, // Venezuela
      'ec': 9,  // Ecuador
      'uy': 8,  // Uruguay
      'py': 9,  // Paraguay
      'bo': 8,  // Bolivia
      'cr': 8,  // Costa Rica
      'pa': 8,  // Panama
      'gt': 8,  // Guatemala
      'hn': 8,  // Honduras
      'ni': 8,  // Nicaragua
      'sv': 8,  // El Salvador
      'do': 10, // Dominican Republic
      'cu': 8,  // Cuba
      'jm': 10, // Jamaica
      'tt': 10, // Trinidad and Tobago
      'bb': 10, // Barbados
      'bs': 10, // Bahamas
      'bz': 7,  // Belize
      'gy': 7,  // Guyana
      'sr': 7,  // Suriname
      'gf': 9,  // French Guiana
      
      // 11-digit numbers (UK, etc.)
      'gb': 11, // United Kingdom
      'ie': 9,  // Ireland
      'nl': 9,  // Netherlands
      'be': 9,  // Belgium
      'lu': 9,  // Luxembourg
      'ch': 9,  // Switzerland
      'at': 10, // Austria
      'de': 11, // Germany
      'dk': 8,  // Denmark
      'se': 9,  // Sweden
      'no': 8,  // Norway
      'fi': 9,  // Finland
      'is': 7,  // Iceland
      'pl': 9,  // Poland
      'cz': 9,  // Czech Republic
      'sk': 9,  // Slovakia
      'hu': 9,  // Hungary
      'ro': 10, // Romania
      'bg': 9,  // Bulgaria
      'gr': 10, // Greece
      'pt': 9,  // Portugal
      'es': 9,  // Spain
      'it': 10, // Italy
      'mt': 8,  // Malta
      'cy': 8,  // Cyprus
      
      // Eastern Europe
      'ua': 9,  // Ukraine
      'by': 9,  // Belarus
      'lt': 8,  // Lithuania
      'lv': 8,  // Latvia
      'ee': 8,  // Estonia
      'md': 8,  // Moldova
      'rs': 9,  // Serbia
      'hr': 9,  // Croatia
      'si': 8,  // Slovenia
      'ba': 8,  // Bosnia and Herzegovina
      'mk': 9,  // North Macedonia
      'al': 9,  // Albania
      'me': 8,  // Montenegro
      'xk': 8,  // Kosovo
      
      // Middle East
      'sa': 9,  // Saudi Arabia
      'ae': 9,  // UAE
      'kw': 8,  // Kuwait
      'qa': 8,  // Qatar
      'bh': 8,  // Bahrain
      'om': 8,  // Oman
      'ye': 9,  // Yemen
      'iq': 10, // Iraq
      'ir': 10, // Iran
      'il': 9,  // Israel
      'jo': 9,  // Jordan
      'lb': 8,  // Lebanon
      'sy': 9,  // Syria
      'ps': 9,  // Palestine
      'tr': 10, // Turkey
      'eg': 10, // Egypt
      'ly': 9,  // Libya
      'tn': 8,  // Tunisia
      'dz': 9,  // Algeria
      'ma': 9,  // Morocco
      'sd': 9,  // Sudan
      'et': 9,  // Ethiopia
      
      // Asia
      'cn': 11, // China
      'jp': 10, // Japan
      'kr': 10, // South Korea
      'tw': 9,  // Taiwan
      'hk': 8,  // Hong Kong
      'mo': 8,  // Macau
      'sg': 8,  // Singapore
      'my': 9,  // Malaysia
      'th': 9,  // Thailand
      'vn': 10, // Vietnam
      'ph': 10, // Philippines
      'id': 9,  // Indonesia
      'mm': 9,  // Myanmar
      'kh': 9,  // Cambodia
      'la': 8,  // Laos
      'bn': 7,  // Brunei
      'bd': 10, // Bangladesh
      'pk': 10, // Pakistan
      'lk': 9,  // Sri Lanka
      'np': 10, // Nepal
      'af': 7,  // Afghanistan
      'uz': 9,  // Uzbekistan
      'tm': 8,  // Turkmenistan
      'tj': 9,  // Tajikistan
      'kg': 9,  // Kyrgyzstan
      'mn': 8,  // Mongolia
      
      // Oceania
      'pg': 8,  // Papua New Guinea
      'fj': 7,  // Fiji
      'nc': 6,  // New Caledonia
      'pf': 6,  // French Polynesia
      'ws': 7,  // Samoa
      'to': 7,  // Tonga
      'vu': 7,  // Vanuatu
      'sb': 7,  // Solomon Islands
      'ki': 5,  // Kiribati
      'tv': 6,  // Tuvalu
      'nr': 7,  // Nauru
      'pw': 7,  // Palau
      'fm': 7,  // Micronesia
      'mh': 7,  // Marshall Islands
      
      // Africa
      'ng': 10, // Nigeria
      'ke': 9,  // Kenya
      'tz': 9,  // Tanzania
      'ug': 9,  // Uganda
      'rw': 9,  // Rwanda
      'gh': 9,  // Ghana
      'ci': 8,  // Côte d'Ivoire
      'sn': 9,  // Senegal
      'ml': 8,  // Mali
      'bf': 8,  // Burkina Faso
      'ne': 8,  // Niger
      'td': 8,  // Chad
      'cm': 9,  // Cameroon
      'ga': 8,  // Gabon
      'cg': 9,  // Republic of the Congo
      'cd': 9,  // DR Congo
      'ao': 9,  // Angola
      'zm': 9,  // Zambia
      'zw': 9,  // Zimbabwe
      'bw': 8,  // Botswana
      'na': 9,  // Namibia
      'mw': 9,  // Malawi
      'mz': 9,  // Mozambique
      'mg': 9,  // Madagascar
      'mu': 8,  // Mauritius
      'sc': 7,  // Seychelles
      'km': 7,  // Comoros
      'dj': 8,  // Djibouti
      'er': 7,  // Eritrea
      'so': 8,  // Somalia
      'ss': 9,  // South Sudan
      
      // Caribbean
      'ht': 8,  // Haiti
      'pr': 10, // Puerto Rico
      'vi': 10, // US Virgin Islands
      'vg': 10, // British Virgin Islands
      'ky': 7,  // Cayman Islands
      'bm': 10, // Bermuda
      'tc': 7,  // Turks and Caicos
      'ai': 7,  // Anguilla
      'ms': 7,  // Montserrat
      'gd': 7,  // Grenada
      'lc': 7,  // Saint Lucia
      'vc': 7,  // Saint Vincent
      'ag': 10,  // Antigua and Barbuda
      'dm': 7,  // Dominica
      'kn': 7,  // Saint Kitts and Nevis
      
      // Other territories
      'fo': 6,  // Faroe Islands
      'gl': 6,  // Greenland
      'sj': 8,  // Svalbard
      'ax': 6,  // Åland Islands
      'gg': 10, // Guernsey
      'je': 10, // Jersey
      'im': 10, // Isle of Man
      'ad': 6,  // Andorra
    };
    
    return countryMaxLengths[regionCode] || 15; // Default to 15 for unknown countries
  }
  
  formatPhoneNumber(phoneNumber: string | number | null | undefined, countryCode?: CountryISO | null): string | null {
    // Convert number to string
    if (typeof phoneNumber === "number") {
      phoneNumber = phoneNumber.toString();
    }

    // If phoneNumber is still invalid, return null
    if (!phoneNumber || typeof phoneNumber !== "string") {
      return null;
    }

    // Use provided countryCode or default to US
    const selectedCountryCode = countryCode || CountryISO.UnitedStates;
    
    if (!selectedCountryCode) {
      return phoneNumber;
    }

    // Remove all non-digit characters for parsing
    const cleanedNumber = phoneNumber.replace(/\D/g, '');
    if (!cleanedNumber || cleanedNumber.length === 0) {
      return phoneNumber;
    }

    const regionCode = String(selectedCountryCode).toLowerCase();
    
    // Special handling for UK to ensure consistent formatting - ALWAYS use this for UK
    if (selectedCountryCode === CountryISO.UnitedKingdom) {
      // UK numbers: 10 digits (mobile) or 11 digits (landline)
      // Format consistently: XXXX XXXXXX (for 10 digits) or XXXX XXX XXXX (for 11 digits)
      if (cleanedNumber.length <= 4) {
        return cleanedNumber;
      } else if (cleanedNumber.length <= 7) {
        return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4)}`;
      } else if (cleanedNumber.length <= 10) {
        // 8-10 digits: XXXX XXXXXX (e.g., 7655 677666)
        return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4)}`;
      } else if (cleanedNumber.length === 11) {
        // 11 digits: XXXX XXX XXXX (e.g., 7655 677 6666)
        return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4, 7)} ${cleanedNumber.slice(7, 11)}`;
      } else {
        // More than 11 digits: truncate to 11 and format
        const truncated = cleanedNumber.substring(0, 11);
        return `${truncated.slice(0, 4)} ${truncated.slice(4, 7)} ${truncated.slice(7, 11)}`;
      }
    }
    
    // Try to use library formatting first (works for all countries)
    try {
      // Parse the phone number with the selected country code
      // This works even for incomplete numbers
      const parsedNumber = this.phoneUtil.parse(cleanedNumber, regionCode);
      
      // Format as national number using library (works for all countries)
      try {
        const formatted = this.phoneUtil.format(parsedNumber, PhoneNumberFormat.NATIONAL);
        // Remove any leading country code that might be included
        let finalFormatted = formatted.replace(/^\+\d+\s*/, '').trim();
        // Remove leading "0" that NATIONAL format adds for some countries (e.g., Afghanistan, UK)
        // Since country code is displayed separately, we don't need this leading "0"
        finalFormatted = finalFormatted.replace(/^0+/, '').trim();
        // Return formatted value (library handles formatting for all countries)
        return finalFormatted || cleanedNumber;
      } catch (formatError) {
        // If NATIONAL format fails, use progressive formatting
        return this.formatPhoneNumberProgressive(cleanedNumber, selectedCountryCode);
      }
    } catch (parseError) {
      // If parsing fails (number might be too incomplete), use progressive formatting
      // This allows user to continue typing with consistent formatting
      return this.formatPhoneNumberProgressive(cleanedNumber, selectedCountryCode);
    }
  }
  
  private formatPhoneNumberProgressive(cleanedNumber: string, countryCode: CountryISO | null | undefined): string {
    if (!countryCode || !cleanedNumber) {
      return cleanedNumber;
    }
    
    // Format progressively based on country
    if (countryCode === CountryISO.UnitedStates) {
      // US format: (XXX) XXX-XXXX
      if (cleanedNumber.length <= 3) {
        return cleanedNumber.length > 0 ? `(${cleanedNumber}` : '';
      } else if (cleanedNumber.length <= 6) {
        return `(${cleanedNumber.slice(0, 3)}) ${cleanedNumber.slice(3)}`;
      } else {
        return `(${cleanedNumber.slice(0, 3)}) ${cleanedNumber.slice(3, 6)}-${cleanedNumber.slice(6, 10)}`;
      }
    } else if (countryCode === CountryISO.UnitedKingdom) {
      // UK format: Consistent formatting for 10 or 11 digits
      if (cleanedNumber.length <= 4) {
        return cleanedNumber;
      } else if (cleanedNumber.length <= 7) {
        return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4)}`;
      } else if (cleanedNumber.length === 10) {
        // 10 digits: XXXX XXXXXX (e.g., 7655 677666)
        return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4)}`;
      } else if (cleanedNumber.length === 11) {
        // 11 digits: XXXX XXX XXXX (e.g., 7655 677 6666)
        return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4, 7)} ${cleanedNumber.slice(7, 11)}`;
      } else {
        // More than 11 digits: truncate to 11 and format
        const truncated = cleanedNumber.substring(0, 11);
        return `${truncated.slice(0, 4)} ${truncated.slice(4, 7)} ${truncated.slice(7, 11)}`;
      }
    }
    
    // Default: return as is
    return cleanedNumber;
  }
  
  private formatPhoneNumberFallback(phoneNumber: string, countryCode: CountryISO | null | undefined): string {
    if (!countryCode) {
      return phoneNumber;
    }
    
    const cleanedNumber = phoneNumber.replace(/\D/g, '');
    const countryCodePrefix = this.getCountryCodePrefix(countryCode);

    // Remove country code prefix if already present
    let digits = cleanedNumber;
    if (countryCodePrefix) {
      const prefixDigits = countryCodePrefix.replace(/\D/g, '');
      if (digits.startsWith(prefixDigits)) {
        digits = digits.substring(prefixDigits.length);
      }
    }

    // Format based on country (without country code prefix since it's displayed separately)
    if (countryCode === CountryISO.UnitedStates && digits.length === 10) {
      const match = digits.match(/^(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return `(${match[1]}) ${match[2]}-${match[3]}`;
      }
    } else if (countryCode === CountryISO.UnitedStates && digits.length > 0) {
      return this.formatPhoneNumberProgressive(digits, countryCode);
    } else if (countryCode === CountryISO.UnitedKingdom && digits.length >= 10) {
      if (digits.length === 11) {
        const match = digits.match(/^(\d{4})(\d{3})(\d{4})$/);
        if (match) {
          return `${match[1]} ${match[2]} ${match[3]}`;
        }
      }
      return this.formatPhoneNumberProgressive(digits, countryCode);
    }

    return digits;
  }
  
  private getCountryCodePrefix(countryCode: CountryISO | null | undefined): string {
    if (!countryCode) {
      return '';
    }
    try {
      const callingCode = this.phoneUtil.getCountryCodeForRegion(String(countryCode).toLowerCase());
      if (callingCode) {
        return `+${callingCode}`;
      }
      return '';
    } catch {
      return '';
    }
  }
  
  // Get country calling code from CountryISO (e.g., CountryISO.Afghanistan -> "+93")
  private getCountryCallingCode(countryCode: CountryISO | null | undefined): string | null {
    if (!countryCode) {
      return null;
    }
    try {
      const callingCode = this.phoneUtil.getCountryCodeForRegion(String(countryCode).toLowerCase());
      return callingCode ? `+${callingCode}` : null;
    } catch {
      return null;
    }
  }
  
  // Convert calling code (e.g., "+93") back to CountryISO (e.g., CountryISO.Afghanistan)
  mapCallingCodeToISO(callingCode: string | number | null): CountryISO | null {
    if (!callingCode) return null;
    
    const codeStr = String(callingCode).replace(/^\+/, '').trim();
    const codeNum = parseInt(codeStr, 10);
    
    if (isNaN(codeNum)) return null;
    
    try {
      const supportedRegions = this.phoneUtil.getSupportedRegions();
      for (const region of supportedRegions) {
        try {
          const regionCallingCode = this.phoneUtil.getCountryCodeForRegion(region);
          if (regionCallingCode === codeNum) {
            return region.toUpperCase() as CountryISO;
          }
        } catch {
          continue;
        }
      }
    } catch (error) {
      
    }
    
    // Fallback: try to match common codes manually
    if (codeNum === 1) return CountryISO.UnitedStates;
    if (codeNum === 44) return CountryISO.UnitedKingdom;
    if (codeNum === 93) return CountryISO.Afghanistan;
    if (codeNum === 355) return CountryISO.Albania;
    
    return null;
  }
  
  // Fetch branch details to get country ID when phone codes are missing
  private fetchBranchDetailsForPhoneCodes(branchId: number, phone: string | null, mobileNumber: string | null) {
    this.systemAdminService.getBranchDetails(String(branchId)).subscribe({
      next: (branchResponse: BranchDetailsResponse) => {
        if (branchResponse) {
          // Extract country ID from branch address
          const countryId = branchResponse?.branchAddress?.countryId ?? null;
          
          if (countryId && this.countryList().length > 0) {
            // Find country from country list
            const selectedCountry = this.countryList().find(c => Number(c.countryId) === Number(countryId));
            
            if (selectedCountry) {
              // Convert country ID to CountryISO
              const countryISO = this.getCountryCodeFromCountryId(countryId);
              
              if (countryISO) {
                // Set both phone and mobile country codes to the branch's country
                // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
                setTimeout(() => {
                  this.userDetailsForm.get('phoneCountryCode')?.setValue(countryISO, { emitEvent: false });
                  this.userDetailsForm.get('mobileNumberCountryCode')?.setValue(countryISO, { emitEvent: false });
                  
                  // Set countryCode (dial code) for backward compatibility
                  const countryCode = selectedCountry?.isdCode ? String(selectedCountry.isdCode) : null;
                  if (countryCode) {
                    this.userDetailsForm.get('countryCode')?.setValue(countryCode);
                  }
                  
                  // Format phone numbers AFTER country codes are set
                  setTimeout(() => {
                    // Double-check that country codes are set in form before formatting
                    const currentPhoneCode = this.userDetailsForm.get('phoneCountryCode')?.value || countryISO;
                    const currentMobileCode = this.userDetailsForm.get('mobileNumberCountryCode')?.value || countryISO;
                    
                    // Format with explicit country codes to ensure correct formatting
                    this.formatPhoneNumbersForBinding(phone, mobileNumber, currentPhoneCode, currentMobileCode);
                    
                    // Re-set country codes after a delay to ensure ngx-intl-tel-input initializes correctly
                    setTimeout(() => {
                      this.userDetailsForm.get('phoneCountryCode')?.setValue(countryISO, { emitEvent: false });
                      this.userDetailsForm.get('mobileNumberCountryCode')?.setValue(countryISO, { emitEvent: false });
                      this.cdr.detectChanges();
                      // Clear binding flag after formatting is complete
                      this.isBinding = false;
                    }, 100);
                  }, 150);
                }, 0);
              } else {
                // Fallback to US if country not found
                this.setDefaultPhoneCodes(phone, mobileNumber);
              }
            } else {
              // Fallback to US if country not found in list
              this.setDefaultPhoneCodes(phone, mobileNumber);
            }
          } else {
            // Fallback to US if country ID not found or country list not loaded
            // Wait a bit for country list to load, then retry
            setTimeout(() => {
              if (countryId && this.countryList().length > 0) {
                const selectedCountry = this.countryList().find(c => Number(c.countryId) === Number(countryId));
                if (selectedCountry) {
                  const countryISO = this.getCountryCodeFromCountryId(countryId);
                  if (countryISO) {
                    setTimeout(() => {
                      this.userDetailsForm.get('phoneCountryCode')?.setValue(countryISO, { emitEvent: false });
                      this.userDetailsForm.get('mobileNumberCountryCode')?.setValue(countryISO, { emitEvent: false });
                      const countryCode = selectedCountry?.isdCode ? String(selectedCountry.isdCode) : null;
                      if (countryCode) {
                        this.userDetailsForm.get('countryCode')?.setValue(countryCode);
                      }
                      setTimeout(() => {
                        // Double-check that country codes are set in form before formatting
                        const currentPhoneCode = this.userDetailsForm.get('phoneCountryCode')?.value || countryISO;
                        const currentMobileCode = this.userDetailsForm.get('mobileNumberCountryCode')?.value || countryISO;
                        
                        // Format with explicit country codes to ensure correct formatting
                        this.formatPhoneNumbersForBinding(phone, mobileNumber, currentPhoneCode, currentMobileCode);
                        setTimeout(() => {
                          this.userDetailsForm.get('phoneCountryCode')?.setValue(countryISO, { emitEvent: false });
                          this.userDetailsForm.get('mobileNumberCountryCode')?.setValue(countryISO, { emitEvent: false });
                          this.cdr.detectChanges();
                          // Clear binding flag after formatting is complete
                          this.isBinding = false;
                        }, 100);
                      }, 150);
                    }, 0);
                    return;
                  }
                }
              }
              this.setDefaultPhoneCodes(phone, mobileNumber);
            }, 200);
          }
        } else {
          // Fallback to US if branch details not found
          this.setDefaultPhoneCodes(phone, mobileNumber);
        }
      },
      error: (error: any) => {
        console.log("Error fetching branch details for phone codes:", error);
        // Fallback to US on error
        this.setDefaultPhoneCodes(phone, mobileNumber);
      }
    });
  }
  
  // Helper method to set default phone codes (US)
  private setDefaultPhoneCodes(phone: string | null, mobileNumber: string | null) {
    const defaultCountryCode = CountryISO.UnitedStates;
    this.userDetailsForm.get('phoneCountryCode')?.setValue(defaultCountryCode, { emitEvent: false });
    this.userDetailsForm.get('mobileNumberCountryCode')?.setValue(defaultCountryCode, { emitEvent: false });
    
    setTimeout(() => {
      this.formatPhoneNumbersForBinding(phone, mobileNumber, defaultCountryCode, defaultCountryCode);
    }, 100);
  }
  
  // Convert country ID to CountryISO
  private getCountryCodeFromCountryId(countryId: number | null): CountryISO | null {
    if (!countryId) return null;
    
    // Common mappings
    if (countryId === 1) return CountryISO.UnitedStates;
    if (countryId === 2) return CountryISO.UnitedKingdom;
    
    // Try to find country in country list and get ISO2 code
    if (this.countryList().length > 0) {
      const country = this.countryList().find(c => Number(c.countryId) === Number(countryId));
      if (country && (country as any).iso2) {
        return (country as any).iso2.toUpperCase() as CountryISO;
      }
      // If no iso2 field, try to use ISD code to find CountryISO
      if (country && country.isdCode) {
        return this.mapCallingCodeToISO(country.isdCode);
      }
    }
    
    return null;
  }

  ngOnDestroy(): void {
    this.phoneSub.unsubscribe();
    this.mobileSub.unsubscribe();
    this.phoneCountryCodeSub.unsubscribe();
    this.mobileCountryCodeSub.unsubscribe();
  }

onUserConfigSubmit(action: boolean) {
  this.changesSaved = true;
  if(this.userConfigForm.invalid){
    return;
  }

  if(action){
    const { isEmailverified, isQrValidated, isResetVerified, isLocked } = this.userConfigForm.getRawValue();
    const payload: UserConfigRequestPayload = {
      isEmailverified: isEmailverified ? 1 : 0, isQrValidated: isQrValidated ? 1 : 0, 
      isResetVerified: isResetVerified ? 1 : 0, isLocked: isLocked ? 1 : 0
    }
    this.updateUserConfigDetails(payload);
  }
}

  private formatPhoneNumbersForBinding(phone: any, mobileNumber: any, phoneCountryCode?: CountryISO, mobileCountryCode?: CountryISO) {
    // Convert phone numbers to string if they're numbers or objects
    let phoneStr: string | null = null;
    let mobileNumberStr: string | null = null;
    
    if (phone !== null && phone !== undefined) {
      if (typeof phone === 'object' && phone.number !== undefined) {
        phoneStr = String(phone.number);
      } else {
        phoneStr = String(phone);
      }
    }
    
    if (mobileNumber !== null && mobileNumber !== undefined) {
      if (typeof mobileNumber === 'object' && mobileNumber.number !== undefined) {
        mobileNumberStr = String(mobileNumber.number);
      } else {
        mobileNumberStr = String(mobileNumber);
      }
    }
    
    // Use provided country codes or get from form (should already be set)
    // Prioritize explicitly passed country codes over form values
    const phoneCountryCodeToUse = phoneCountryCode || this.userDetailsForm.get('phoneCountryCode')?.value || CountryISO.UnitedStates;
    const mobileCountryCodeToUse = mobileCountryCode || this.userDetailsForm.get('mobileNumberCountryCode')?.value || CountryISO.UnitedStates;
    
    // Format phone numbers (string format when separateDialCode is true)
    // For binding, extract raw digits first (remove any existing formatting or country code prefix)
    let phoneRaw = phoneStr ? this.extractRawPhoneNumber(phoneStr, phoneCountryCodeToUse) : null;
    let mobileRaw = mobileNumberStr ? this.extractRawPhoneNumber(mobileNumberStr, mobileCountryCodeToUse) : null;
    
    // Special handling for UK: if 11 digits, cut the last digit (UK numbers should be 10 digits for mobile or 11 with leading 0)
    if (phoneCountryCodeToUse === CountryISO.UnitedKingdom && phoneRaw && phoneRaw.length === 11 && !phoneRaw.startsWith('0')) {
      phoneRaw = phoneRaw.substring(0, 10);
    }
    if (mobileCountryCodeToUse === CountryISO.UnitedKingdom && mobileRaw && mobileRaw.length === 11 && !mobileRaw.startsWith('0')) {
      mobileRaw = mobileRaw.substring(0, 10);
    }
    
    // Format the raw numbers according to country code
    // Ensure we're using the correct country codes for formatting
    const formattedPhone = phoneRaw ? this.formatPhoneNumber(phoneRaw, phoneCountryCodeToUse) : null;
    const formattedMobile = mobileRaw ? this.formatPhoneNumber(mobileRaw, mobileCountryCodeToUse) : null;
    
    // Set as string values (when separateDialCode is true)
    // Set values directly - this method is already called within setTimeout
    if (formattedPhone !== null && formattedPhone !== undefined && formattedPhone !== '') {
      this.userDetailsForm.get('phone')?.setValue(formattedPhone, { emitEvent: false });
    } else if (phoneRaw) {
      // If formatting returned null/empty but we have raw digits, set raw digits
      this.userDetailsForm.get('phone')?.setValue(phoneRaw, { emitEvent: false });
    } else if (phoneStr) {
      // Fallback to original string
      this.userDetailsForm.get('phone')?.setValue(phoneStr, { emitEvent: false });
    }
    
    if (formattedMobile !== null && formattedMobile !== undefined && formattedMobile !== '') {
      this.userDetailsForm.get('mobileNumber')?.setValue(formattedMobile, { emitEvent: false });
    } else if (mobileRaw) {
      // If formatting returned null/empty but we have raw digits, set raw digits
      this.userDetailsForm.get('mobileNumber')?.setValue(mobileRaw, { emitEvent: false });
    } else if (mobileNumberStr) {
      // Fallback to original string
      this.userDetailsForm.get('mobileNumber')?.setValue(mobileNumberStr, { emitEvent: false });
    }
  }

isFormDirty(): boolean {
  return JSON.stringify(this.initialFormValue) !== JSON.stringify(this.userDetailsForm.getRawValue());
}

canDeactivate(): boolean {
  return this.changesSaved || !this.isFormDirty();
}
}
